// BracketMilestone — a full-screen event when the player crosses an age boundary
// (11 / 14 / 17). A new life stage; the Vision Board re-opens after this and, at
// the Explorer stage, the Mastermind unlocks.

import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { bracketName } from '@biz-kid/engine';
import { Button } from '../ui/kit.js';
import { useVoice } from '../voice/VoiceContext.js';

const STAGE_BLURB: Record<string, string> = {
  Explorer: 'YOUR CREW is unlocked. Aim higher.',
  Builder: 'Bigger moves now. Stack real machines.',
  Founder: 'Home stretch. Build a legacy.',
};

export function BracketMilestone({ newAge, onContinue }: { newAge: number; onContinue: () => void }) {
  const stage = bracketName(visionBracketLocal(newAge));

  // MIKO SPEAKS — the level-up is a celebration; shout it once.
  const { speak } = useVoice();
  const spokeRef = useRef(false);
  useEffect(() => {
    if (spokeRef.current) return;
    spokeRef.current = true;
    void speak(`Level up! You're ${newAge} — welcome to the ${stage} stage. ${STAGE_BLURB[stage] ?? 'Bigger dreams, bigger moves.'}`);
  }, [speak, newAge, stage]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 82, display: 'grid', placeItems: 'center', padding: 22, background: 'linear-gradient(160deg, var(--brand-strong), var(--brand))', overflow: 'hidden' }}>
      {/* drifting sparkles */}
      {Array.from({ length: 16 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: [0, 0.8, 0], y: -40 }}
          transition={{ duration: 3, delay: (i % 8) * 0.25, repeat: Infinity }}
          style={{ position: 'absolute', left: `${(i * 61) % 100}%`, top: `${(i * 37) % 100}%`, fontSize: 16 }}
        >
          ✨
        </motion.div>
      ))}
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 14 }} style={{ textAlign: 'center', maxWidth: 420, position: 'relative' }}>
        <div style={{ fontSize: 64 }}>⬆️</div>
        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)' }}>
          LEVEL UP
        </div>
        <h1 style={{ margin: '6px 0 0', fontSize: 32, fontWeight: 900, color: '#fff' }}>YOU’RE {newAge}!</h1>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginTop: 4 }}>{stage.toUpperCase()} STAGE</div>
        <p style={{ margin: '14px auto 0', maxWidth: 360, fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.95)', fontWeight: 700 }}>
          {STAGE_BLURB[stage] ?? 'Bigger dreams. Bigger moves.'}
        </p>
        <div style={{ marginTop: 22 }}>
          <Button tone="soft" size="lg" onClick={onContinue} style={{ background: '#fff', color: 'var(--brand-strong)' }}>LET’S GO →</Button>
        </div>
      </motion.div>
    </div>
  );
}

// Local copy of the bracket math (avoids an extra import round-trip for one call).
function visionBracketLocal(age: number): number {
  if (age <= 10) return 1;
  if (age <= 13) return 2;
  if (age <= 16) return 3;
  return 4;
}
